const defaultCircleRadiusGenerator = (radius = null, hoverRadius = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "hover"], false],
    hoverRadius ?? 9,
    ["boolean", ["get", "editing"], false],
    7,
    ["boolean", ["get", "pseudoHover"], false],
    9,
    ["boolean", ["feature-state", "selected"], false],
    7,
    radius ?? 5,
  ];
};

const relativeSizeCircleRadiusGenerator = (steps) => {
  return [
    "step",
    ["get", "count"],
    steps[0],
    10,
    steps[1],
    100,
    steps[2],
    1000,
    steps[3],
  ];
};

const defaultCircleColorGenerator = (fill = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#008000",
    fill || "#6a3d9a",
  ];
};

const interpolatedCircleColorGenerator = () => {
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#558C63",
    [
      "interpolate-lab",
      ["linear"],
      ["get", "interpolatePercentage"],
      0,
      "#ff0000",
      1,
      "#36729e",
    ],
  ];
};

const defaultCircleStrokeColorGenerator = (stroke = null) => {
  return [
    "case",
    ["boolean", ["feature-state", "selected"], false],
    "#558C63",
    stroke || "#6a3d9a",
  ];
};

const defaultConfig = {
  checkGroup: false,
  radiusSystem: "default",
  colorSystem: "default",
  radiusSteps: [10, 20, 30, 40],
};

const pointStyleGenerator = (config = defaultConfig) => {
  return {
    type: "circle",
    paint: {
      "circle-radius":
        config.radiusSystem === "relative"
          ? relativeSizeCircleRadiusGenerator(config.radiusSteps)
          : defaultCircleRadiusGenerator(config.radius, config.hoverRadius),
      "circle-color":
        config.colorSystem === "default"
          ? defaultCircleColorGenerator(config.fill)
          : interpolatedCircleColorGenerator(),
      "circle-opacity": [
        "case",
        ["boolean", ["get", "disabled"], false],
        0.3,
        ["boolean", ["feature-state", "selected"], ["get", "editing"], false],
        0.8,
        ["boolean", ["feature-state", "hover"], false],
        0.6,
        ["boolean", ["get", "inGroup"], false],
        config.checkGroup ? 0.5 : 0.4,
        config.checkGroup ? 0.3 : 0.4,
      ],
      "circle-stroke-color":
        config.colorSystem === "default"
          ? defaultCircleStrokeColorGenerator(config.stroke || config.fill)
          : interpolatedCircleColorGenerator(),
      "circle-stroke-opacity": [
        "case",
        ["get", "disabled"],
        0.2,
        ["boolean", ["get", "inGroup"], false],
        1,
        config.checkGroup ? 0.8 : 1,
      ],
      "circle-stroke-width": config.strokeWidth ?? 1.5,
    },
  };
};

export const symbolStyleGenerator = () => {
  return {
    type: "symbol",
    layout: {
      "icon-image": ["get", "iconName"],
      "icon-allow-overlap": true,
      "icon-size": ["number", ["get", "iconSize"], 0.5],
    },
    paint: {
      "icon-color": ["get", "iconColor"],
      "icon-opacity": 0.6,
    },
  };
};

export const symbolStyleGeneratorNoColor = () => {
  return {
    type: "symbol",
    layout: {
      "icon-image": ["get", "iconName"],
      "icon-allow-overlap": true,
      "icon-size": ["number", ["get", "iconSize"], 0.5],
    },
    paint: {
      "icon-opacity": 0.6,
    },
  };
};

// IF YOU NEED MULTIPLE LAYERS WITH THE SAME STYLE, SET THE ID FOR THE STYLE IN THE BASE MAP OF THE COMPONENT, SEE TRACKERMAP2.JS FOR A WORKING EXAMPLE
export const defaultPointStyle = {
  id: "defaultPointLayer",
  ...pointStyleGenerator(),
};

export const onlyHoverPointStyle = {
  id: "onlyHoverPointStyle",
  ...pointStyleGenerator({
    ...defaultConfig,
    radius: 0,
    hoverRadius: 0,
    strokeWidth: 0,
  }),
};

export const relativeSizePointStyle = {
  id: "relativeSizePointStyle",
  ...pointStyleGenerator({
    ...defaultConfig,
    radiusSystem: "relative",
    colorSystem: "interpolate",
  }),
};

export const groupedPointStyle = {
  id: "routePointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    checkGroup: true,
  }),
};

export const groupedRelativePointStyle = {
  id: "groupedRelativePointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    radiusSystem: "relative",
    checkGroup: true,
    radiusSteps: [4, 6, 8, 10],
  }),
};

export const mapReportsStyle = {
  id: "eventPointLayer",
  ...pointStyleGenerator({
    ...defaultConfig,
    fill: ["get", "groupColor"],
    radius: 4,
  }),
};


export const searchPointStyle = {
  id: "searchPointLayer",
  ...pointStyleGenerator({ ...defaultConfig, fill: "#f00" }),
};

export const labelStyleGenerator = (positioning = "default") => {
  return {
    type: "symbol",
    layout: {
      "text-field": ["get", "label"],
      "text-anchor": "top",
      "text-offset":
        positioning === "default"
          ? [0, 0.5]
          : [
              "step",
              ["get", "count"],
              // literal is a keyword required for when working with logical expressions that include literal arrays
              // in this weird little styling language
              ["literal", [0, 1]],
              10,
              ["literal", [0, 1.3]],
              100,
              ["literal", [0, 1.6]],
              1000,
              ["literal", [0, 2.1]],
            ],
      "text-size": ["case", ["get", "disabled"], 0, 12],
    },
    paint: {
      "text-halo-color": "white",
      "text-halo-width": 1,
    },
  };
};

export const labelStyle = {
  id: "labelStyle",
  ...labelStyleGenerator(),
};

export const relativeLabelStyle = {
  id: "relativeLabelStyle",
  ...labelStyleGenerator("relative"),
};

export const internalCountStyle = {
  id: "internalCountStyle",
  type: "symbol",
  layout: {
    "text-field": ["get", "count"],
    "text-anchor": "center",
    "text-size": 12,
  },
  paint: {
    "text-halo-color": "white",
    "text-halo-width": 1,
  },
};

export const gradientStyleGenerator = () => {
  return {
    type: "line",
    paint: {
      "line-gradient": [
        "interpolate",
        ["linear"],
        ["line-progress"],
        0,
        "#3d5941",
        0.15,
        "#778868",
        0.3,
        "#b5b991",
        0.45,
        "#d2b61d",
        0.6,
        "#edbb8a",
        0.75,
        "#de8a5a",
        1,
        "#ca562c",
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        3,
        2,
      ],
    },
  };
};

export const lineStyleGenerator = () => {
  return {
    type: "line",
    paint: {
      "line-color": ["string", ["get", "lineColor"], "#9b4dca"],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        3,
        1,
      ],
    },
  };
};

export const polyStyleGenerator = (color = null) => {
  return {
    type: "fill",
    paint: {
      "fill-opacity": 0.3,
      // 'fill-color': color || '#9b4dca',

      "fill-color": ["string", ["get", "polyColor"], "#9b4dca"],
    },
  };
};


export const timelineStyle = {
  ...gradientStyleGenerator(),
  id: "timeline-line",
};

export const gradientLineStyle = {
  id: "gradientLineStyle",
  ...gradientStyleGenerator(),
};

export const defaultLineStyle = {
  id: "defaultLineStyle",
  ...lineStyleGenerator(),
};

export const polyStrokeStyle = {
  id: "polyStrokeStyle",
  ...lineStyleGenerator(),
};

export const defaultSymbolStyle = {
  id: "defaultSymbolLayer",
  ...symbolStyleGenerator(),
};

export const homeFilledSymbolStyle = {
  id: "homeFilledSymbolLayer",
  ...symbolStyleGeneratorNoColor(),
};

export const defaultPolyStyle = {
  id: "defaultPolyStyle",
  ...polyStyleGenerator(),
};
